import React, { useMemo } from 'react';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
  useFilters
} from 'react-table';
import { Button, Card, Form, Badge } from 'react-bootstrap';
import {
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaFileExcel,
  FaCheckCircle,
  FaLock,
  FaPaperclip,
  FaPaperPlane
} from 'react-icons/fa';
import * as XLSX from 'xlsx';

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <div className="d-flex align-items-center gap-2 mb-3">
      <FaSearch />
      <Form.Control
        style={{ maxWidth: 320, borderRadius: 10 }}
        value={globalFilter || ''}
        onChange={(e) => setGlobalFilter(e.target.value || undefined)}
        placeholder="Recherche globale..."
      />
    </div>
  );
};

const SelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id }
}) => {
  const options = React.useMemo(() => {
    const opts = new Set();
    preFilteredRows.forEach((row) => {
      opts.add(row.values[id]);
    });
    return [...opts];
  }, [id, preFilteredRows]);

  return (
    <Form.Select
      size="sm"
      value={filterValue || ''}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      style={{ minWidth: 120, borderRadius: 8 }}
    >
      <option value="">Tous</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </Form.Select>
  );
};

const DefaultColumnFilter = ({
  column: { filterValue, setFilter, placeholder = 'Filtrer...' }
}) => {
  return (
    <Form.Control
      size="sm"
      value={filterValue || ''}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={placeholder}
      style={{ minWidth: 100, borderRadius: 8 }}
    />
  );
};

const InlineInput = ({ row, field, color, onCellChange }) => {
  const isLocked = row.original.dejaPresent || row.original.bloque;
  const value = row.original[field] ?? '';

  if (isLocked) {
    return (
      <span style={{ color: '#aaa', fontSize: 13, padding: '4px 8px', display: 'block', textAlign: 'center' }}>
        {value !== '' ? value : '—'}
      </span>
    );
  }

  return (
    <Form.Control
      type="number"
      min="0"
      step="0.5"
      value={value}
      onChange={(e) => onCellChange(row.original.id, field, e.target.value)}
      style={{
        width: 80,
        textAlign: 'center',
        borderRadius: 8,
        border: `1.5px solid ${color}`,
        fontSize: 13,
        fontWeight: 600,
        padding: '4px 6px',
        background: '#fff'
      }}
    />
  );
};

const TableComponent = ({
  data = [],
  onCellChange,
  onOpenDocuments,
  onSendToTout,
  title = 'Liste',
  showExport = true
}) => {

  const exportExcel = () => {
    const rows = data.map((row) => ({
      ID: row.id,
      CIN: row.cin,
      Nom: row.name,
      Qualification: row.qualification,
      Matricule_Sage: row.matricule_sage || '',
      Chantier: row.chantier_actuel || '',
      Jours: row.jours ?? '',
      Heures: row.heures ?? '',
      Bloque: row.bloque ? 'Oui' : 'Non',
      DejaPointe: row.dejaPresent ? 'Oui' : 'Non',
      Observation: row.observation || ''
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PointageJournalier');
    XLSX.writeFile(wb, 'pointage_journalier.xlsx');
  };

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        Filter: DefaultColumnFilter,
        width: 60
      },
      {
        Header: 'CIN',
        accessor: 'cin',
        Filter: DefaultColumnFilter
      },
      {
        Header: 'Nom',
        accessor: 'name',
        Filter: DefaultColumnFilter
      },
      {
        Header: 'Qualification',
        accessor: 'qualification',
        Filter: DefaultColumnFilter
      },
      {
        Header: 'Matricule Sage',
        accessor: 'matricule_sage',
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => value || '—'
      },
      {
        Header: 'Chantier',
        accessor: 'chantier_actuel',
        Filter: SelectColumnFilter,
        Cell: ({ value }) => value || '—'
      },
      {
        Header: 'Jours',
        accessor: 'jours',
        disableFilters: true,
        width: 75,
        centerContent: true,
        Cell: ({ row }) => <InlineInput row={row} field="jours" color="#bee5ff" onCellChange={onCellChange} />
      },
      {
        Header: 'Heures',
        accessor: 'heures',
        disableFilters: true,
        width: 75,
        centerContent: true,
        Cell: ({ row }) => <InlineInput row={row} field="heures" color="#bfe7cf" onCellChange={onCellChange} />
      },
      {
        Header: 'Docs',
        accessor: 'docs',
        disableSortBy: true,
        disableFilters: true,
        width: 55,
        centerContent: true,
        Cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline-primary"
            style={{ borderRadius: 10 }}
            onClick={() => onOpenDocuments?.(row.original)}
          >
            <FaPaperclip />
          </Button>
        )
      },
      {
        Header: 'Statut',
        accessor: 'statut_affichage',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => {
          if (row.original.bloque) {
            return (
              <Badge bg="danger" className="px-2 py-2">
                <FaLock className="me-1" />
                Bloqué
              </Badge>
            );
          }

          if (row.original.dejaPresent) {
            return (
              <Badge bg="secondary" className="px-2 py-2">
                <FaCheckCircle className="me-1" />
                Déjà pointé
              </Badge>
            );
          }

          return (
            <Badge bg="success" className="px-2 py-2">
              À saisir
            </Badge>
          );
        }
      },
      ...(onSendToTout ? [{
        Header: 'Actions',
        accessor: 'actions_tout',
        disableSortBy: true,
        disableFilters: true,
        width: 90,
        centerContent: true,
        Cell: ({ row }) => {
          if (row.original.bloque || Number(row.original.chantier_id) === 16) return null;
          return (
            <Button
              variant="outline-secondary"
              size="sm"
              style={{ borderRadius: 10 }}
              onClick={() => onSendToTout(row.original.id)}
              title="Envoyer vers chantier TOUT"
            >
              <FaPaperPlane className="me-1" /> TOUT
            </Button>
          );
        }
      }] : [])
    ],
    [onOpenDocuments, onCellChange, onSendToTout]
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn,
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetPage: false,
      autoResetGlobalFilter: false,
      initialState: { pageSize: 100 }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize
  } = tableInstance;

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <>
      <Card
        className="mb-3"
        style={{
          borderRadius: '16px',
          border: '1px solid rgba(109,78,54,0.18)',
          boxShadow: '0 8px 24px rgba(109,78,54,0.08)'
        }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div style={{ fontWeight: 700, color: '#6d4e36' }}>{title}</div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {showExport && (
                <Button variant="outline-success" onClick={exportExcel}>
                  <FaFileExcel className="me-2" />
                  Export Excel
                </Button>
              )}

              <Form.Select
                size="sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{ width: 110, borderRadius: 10 }}
              >
                {[20, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />

          <div className="table-responsive" style={{ maxHeight: 600, overflowY: 'auto', borderRadius: 10, border: '1px solid #e8ddd4' }}>
            <table
              {...getTableProps()}
              className="table table-hover table-sm align-middle"
              style={{ marginBottom: 0, fontSize: 13, borderCollapse: 'separate', borderSpacing: 0 }}
            >
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                {headerGroups.map((headerGroup) => (
                  <React.Fragment key={headerGroup.getHeaderGroupProps().key}>
                    <tr {...headerGroup.getHeaderGroupProps()} className="table-dark">
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          key={column.id}
                          style={{
                            verticalAlign: 'middle',
                            whiteSpace: 'nowrap',
                            width: column.width || undefined,
                            minWidth: column.width || 100,
                            maxWidth: column.width || undefined,
                            textAlign: column.centerContent ? 'center' : 'left',
                            fontSize: 12,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            padding: '8px 10px'
                          }}
                        >
                          <div className={`d-flex align-items-center gap-1 ${column.centerContent ? 'justify-content-center' : 'justify-content-between'}`}>
                            <span>{column.render('Header')}</span>
                            {!column.disableSortBy && (
                              <span style={{ opacity: column.isSorted ? 1 : 0.45, fontSize: 10 }}>
                                {column.isSorted ? (column.isSortedDesc ? '▼' : '▲') : '⇅'}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>

                    <tr className="table-secondary">
                      {headerGroup.headers.map((column) => (
                        <th key={`${column.id}-filter`} style={{ padding: '4px 6px' }}>
                          {column.canFilter ? column.render('Filter') : null}
                        </th>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={99} className="text-center text-muted py-4" style={{ background: '#fafafa' }}>
                      Aucun résultat
                    </td>
                  </tr>
                ) : page.map((row, rowIndex) => {
                  prepareRow(row);

                  const isBloque = row.original.bloque;
                  const isDeja = row.original.dejaPresent;
                  const isEven = rowIndex % 2 === 0;

                  const rowBg = isBloque
                    ? '#fde8e8'
                    : isDeja
                      ? '#e8eaf0'
                      : isEven ? '#fff' : '#faf7f4';

                  return (
                    <tr
                      {...row.getRowProps()}
                      key={row.getRowProps().key}
                      style={{
                        backgroundColor: rowBg,
                        opacity: (isBloque || isDeja) ? 0.78 : 1,
                        transition: 'background 0.15s'
                      }}
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.getCellProps().key}
                          style={{
                            verticalAlign: 'middle',
                            textAlign: cell.column.centerContent ? 'center' : 'left',
                            padding: '5px 8px',
                            borderBottom: '1px solid #ede5dc',
                            borderRight: '1px solid #f0e8e0'
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
            <div className="text-muted small">
              Page <strong>{pageIndex + 1}</strong> / <strong>{pageOptions.length || 1}</strong>
              <span className="ms-2" style={{ color: '#aaa' }}>({data.length} ligne{data.length > 1 ? 's' : ''})</span>
            </div>

            <div className="d-flex gap-1 flex-wrap">
              <Button variant="outline-secondary" size="sm" onClick={() => gotoPage(0)} disabled={!canPreviousPage} style={{ borderRadius: 8 }}>
                <FaAngleDoubleLeft />
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage} style={{ borderRadius: 8 }}>
                <FaChevronLeft />
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => nextPage()} disabled={!canNextPage} style={{ borderRadius: 8 }}>
                <FaChevronRight />
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => gotoPage(pageOptions.length - 1)}
                disabled={!canNextPage}
                style={{ borderRadius: 8 }}
              >
                <FaAngleDoubleRight />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

    </>
  );
};

export default TableComponent;